/**
 * **CashSync** ads — domain layer (the ONLY app-specific part).
 *
 * The rest of the kit (`types`/`gate`/`caps`/`config`/`provider`/`placements`/
 * `rewards`) is generic and copied as-is from canon (`listto`, `876e3b27`);
 * this file is what each app rewrites with its row from the "plan per app" table
 * of `infra/guides/ADS_STRATEGY.md`.
 *
 * CashSync row (§10, row 6 — tier **A**):
 *   - breakpoints: **month-close / reviewed budget** (`month-close`) and
 *     **stats** (native);
 *   - rewarded: premium theme 24 h, +CSV/reports export quota 24 h, and
 *     daily logging streak freeze.
 *
 * ⚠️ **Every placement in this app also passes through `monetization/ads-policy`**,
 * which is the HARD policy of the money domain (written and tested in previous wave):
 * no banner, never next to editable amounts, and **never** behavioral targeting
 * on financial data. The generic kit decides *if possible* and *if due*;
 * `ads-policy` decides *if domain permits*. All three layers must say yes.
 * `assertCashsyncAdPolicy()` is the guard preventing declaration of placements
 * not permitted by policy.
 *
 * Rewards **reuse existing value** (gamification engine, theme catalog,
 * and CSV/XLSX export) instead of inventing a new economy, and **none is permanent**:
 * permanence belongs to premium (§5).
 */

import type { AdPolicyVerdict, AdRequest } from "../monetization/ads-policy";
import { evaluateAdPolicy } from "../monetization/ads-policy";
import { type AdCapsStorage, adDayKey } from "./caps";
import { definePlacements, placementDef } from "./placements";
import type { AdRewardsStore } from "./rewards";
import type { AdDayKey, AdTier } from "./types";

/** CashSync ad tier (§9): consumption, all formats… except banner. */
export const CASHSYNC_AD_TIER: AdTier = "A";

/** Reward types (stable ids: stored in local storage). */
export const REWARD_STREAK_FREEZE = "streak-freeze";
export const REWARD_THEME_UNLOCK = "theme-unlock";
export const REWARD_EXPORT_QUOTA = "export-quota";

/** Extra exports granted by rewarded redemption (temporary, 24 h). */
export const EXPORT_QUOTA_BONUS = 5;

/**
 * Exports/day for ad-supported tier. **Only applies when monetization
 * is enabled AND user tier includes ads** — with flags OFF (factory default)
 * export remains unlimited, byte-for-byte as before this wave.
 */
export const FREE_DAILY_EXPORTS = 3;

/**
 * "Signature" themes from CashSync catalog (`core_rust::themes`). Unlocked
 * for 24 h via rewarded ad ("try it; keep it with premium").
 *
 * List is deliberately EXPLICIT, not a rule derived from data: a rule like
 * "all themes with decorative pattern are premium" would block 12 of 14 catalog
 * themes, turning a trial into a wall. Both entire families (light + dark) are declared
 * so light/dark pairing never leaves user with half a theme locked.
 */
export const PREMIUM_THEME_IDS: readonly string[] = [
  "cashsync-prosperity-light",
  "cashsync-prosperity-dark",
  "cashsync-vault-light",
  "cashsync-vault-dark",
];

/**
 * CashSync placement registry. Source of truth: a breakpoint not listed
 * here cannot render an ad.
 *
 * **No banner** — `ads-policy` forbids it in this app (persistent banner
 * would coexist with transaction and amount listings), and policy test binds it:
 * declaring one breaks test build even if tier A permitted it in abstract.
 */
export const CASHSYNC_AD_PLACEMENTS = definePlacements({
  /**
   * Month-close / reviewed budget: user returned to an ALREADY CLOSED month
   * in reports and is reviewing it. Pure passive consumption (reports page
   * edits no amount) and a closed unit of value — canonical breather for this app.
   */
  "month-close": { kind: "interstitial", breakpoint: "done-screen" },
  /** Period stats: passive consumption ⇒ integrated native card. */
  "monthly-stats": { kind: "native", breakpoint: "stats" },
  /** Daily logging streak: "save your streak" (the Duolingo case). */
  "streak-save": {
    kind: "rewarded",
    breakpoint: "reward-moment",
    reward: { type: REWARD_STREAK_FREEZE, amount: 1 },
  },
  /** Try a signature theme for 24 h. */
  "theme-trial": {
    kind: "rewarded",
    breakpoint: "reward-moment",
    reward: { type: REWARD_THEME_UNLOCK, amount: 1, durationHours: 24 },
  },
  /** Increases CSV/XLSX export quota for 24 h (limit resets automatically). */
  "export-boost": {
    kind: "rewarded",
    breakpoint: "reward-moment",
    reward: { type: REWARD_EXPORT_QUOTA, amount: EXPORT_QUOTA_BONUS, durationHours: 24 },
  },
} as const);

export type CashsyncPlacementId = keyof typeof CASHSYNC_AD_PLACEMENTS;

/**
 * **Declared surface** of each placement — context consumed by `ads-policy`.
 * None may be in `FORBIDDEN_AD_SURFACES` (amount editors). Kept as a separate map
 * rather than a field of `PlacementDef` to avoid diverging from canon type:
 * generic kit is copied as-is across 28 clones.
 */
export const CASHSYNC_AD_SURFACES: Record<CashsyncPlacementId, string> = {
  "month-close": "reports-month-review",
  "monthly-stats": "reports-stats",
  "streak-save": "profile-gamification",
  "theme-trial": "theme-gallery",
  "export-boost": "data-export",
};

/** Context options to evaluate policy at runtime. */
export interface CashsyncAdContext {
  /** true while an amount is being edited on screen (the form is open). */
  amountBeingEdited?: boolean;
  /** Targeting signals placement wants to send to network. */
  targeting?: readonly string[];
}

/**
 * Verdict of CashSync HARD policy for a registered placement.
 * Consulted by `AdsProvider` BEFORE `adGate`: if domain says no, capping
 * is not even evaluated.
 */
export function evaluateCashsyncPlacement(
  placementId: string,
  context: CashsyncAdContext = {},
): AdPolicyVerdict {
  const def = placementDef(CASHSYNC_AD_PLACEMENTS, placementId);
  if (!def) {
    // An undeclared placement does not exist. Closed via strictest
    // door available: no format to approve.
    return { allowed: false, reason: "forbidden-kind" };
  }
  const surfaces: Record<string, string> = CASHSYNC_AD_SURFACES;
  const request: AdRequest = {
    kind: def.kind,
    surface: surfaces[placementId] ?? placementId,
    ...(context.targeting ? { targeting: context.targeting } : {}),
    ...(context.amountBeingEdited === undefined
      ? {}
      : { amountBeingEdited: context.amountBeingEdited }),
  };
  return evaluateAdPolicy(request);
}

export interface CashsyncPolicyViolation {
  placementId: string;
  reason: AdPolicyVerdict["reason"];
}

/**
 * Guard: **every** declared placement passes domain policy. Enforced by test
 * (§7.4 "integration"), not an informal convention written in an unread guide.
 */
export function validateCashsyncAdPolicy(): CashsyncPolicyViolation[] {
  const violations: CashsyncPolicyViolation[] = [];
  for (const placementId of Object.keys(CASHSYNC_AD_PLACEMENTS)) {
    const verdict = evaluateCashsyncPlacement(placementId);
    if (!verdict.allowed) {
      violations.push({ placementId, reason: verdict.reason });
    }
  }
  return violations;
}

/** Test/bootstrap sugar: throws with detail if there are violations. */
export function assertCashsyncAdPolicy(): void {
  const violations = validateCashsyncAdPolicy();
  if (violations.length > 0) {
    const detail = violations.map((v) => `  · ${v.placementId}: ${v.reason}`).join("\n");
    throw new Error(`Placements that violate CashSync's ads policy:\n${detail}`);
  }
}

// ── Hooks into EXISTING economy ──────────────────────────────────────────────

/**
 * Wiring with the gamification engine: streak freezes earned through
 * rewards are the `freezes` consumed by `computeStreak`. They are read from the rewards
 * store (local-first ⇒ works for guests and offline).
 */
export function adFreezes(store: AdRewardsStore, now: number = Date.now()): number {
  return store.amountOf(REWARD_STREAK_FREEZE, now);
}

/** Is there a signature theme trial active right now? */
export function themeTrialActive(store: AdRewardsStore, now: number = Date.now()): boolean {
  return store.hasUnlock(REWARD_THEME_UNLOCK, now);
}

/** Is this theme one of the signature themes (unlocked by rewarded ad)? */
export function isPremiumTheme(themeId: string | null | undefined): boolean {
  return themeId ? PREMIUM_THEME_IDS.includes(themeId) : false;
}

export interface ThemeLockContext {
  /** Is ad layer active? (flags + tier + network). */
  adsLive: boolean;
  /** Is user in a tier WITHOUT ads (premium)? */
  premium: boolean;
  store: AdRewardsStore;
}

/**
 * Is this theme locked for the user?
 *
 * **With ads disabled (factory default) NOTHING is locked**: the gate only
 * exists where the ad economy that opens it exists. Premium has all of them.
 */
export function isThemeLocked(
  themeId: string | null | undefined,
  ctx: ThemeLockContext,
  now: number = Date.now(),
): boolean {
  if (!ctx.adsLive || ctx.premium || !isPremiumTheme(themeId)) {
    return false;
  }
  return !themeTrialActive(ctx.store, now);
}

/**
 * Daily export limit, with TEMPORARY rewarded bonus added.
 * `limit === null` (unlimited) is respected as-is — adding a bonus to "no
 * limit" is meaningless.
 */
export function exportLimitWithAdBonus(
  limit: number | null,
  store: AdRewardsStore,
  now: number = Date.now(),
): number | null {
  if (limit === null) {
    return null;
  }
  return limit + store.amountOf(REWARD_EXPORT_QUOTA, now);
}

// ── Daily export budget (local-first) ────────────────────────────────────────

/** Canonical key for daily export counter. */
export const EXPORT_BUDGET_KEY = "ads.export";

/**
 * Daily export counter, stored on device. Same pattern as
 * `AdCapsStore`: daily key, falls back to "clean day" on storage failure, and
 * works **without account and offline** (a quota requiring a session fails in a
 * local-first app).
 *
 * Client-side enforcement by design: export DOES NOT go through server
 * (built from local SQLite), so no other place to measure it exists.
 * It is a product limit, not a security control.
 */
export class ExportBudgetStore {
  constructor(
    private readonly storage: AdCapsStorage,
    private readonly scope = "device",
  ) {}

  private key(day: AdDayKey): string {
    return `${EXPORT_BUDGET_KEY}.${this.scope}.${day}`;
  }

  /** Exportaciones ya hechas hoy. */
  usedToday(now: number = Date.now()): number {
    try {
      const raw = this.storage.getItem(this.key(adDayKey(now)));
      const parsed = raw === null ? Number.NaN : Number.parseInt(raw, 10);
      return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
    } catch {
      return 0;
    }
  }

  /** Records a consumed export. */
  record(now: number = Date.now()): void {
    try {
      this.storage.setItem(this.key(adDayKey(now)), String(this.usedToday(now) + 1));
    } catch {
      // Storage full or private mode: the export is NOT blocked by not being able to
      // count. Failing open ("allow export") is the only acceptable
      // direction when the user's own data is at stake.
    }
  }

  /** After a premium purchase the quota no longer makes sense. */
  reset(now: number = Date.now()): void {
    try {
      this.storage.removeItem(this.key(adDayKey(now)));
    } catch {
      // no consequences
    }
  }
}

export interface ExportBudgetContext {
  /** Is ad layer active? If not, no limit applies. */
  adsLive: boolean;
  /** Is user in a tier WITHOUT ads (premium)? */
  premium: boolean;
  rewards: AdRewardsStore;
  budget: ExportBudgetStore;
}

export interface ExportAllowance {
  allowed: boolean;
  used: number;
  /** `null` = unlimited. */
  limit: number | null;
  /** Active extra units granted by rewarded ad (0 if no redemption). */
  bonus: number;
}

/**
 * Can user export NOW? Also returns numbers to explain the status
 * (never a bare "no" without context).
 *
 * **Ads off ⇒ unlimited.** Same rule as the rest of the kit: without
 * flags, app behaves exactly as before.
 */
export function evaluateExportAllowance(
  ctx: ExportBudgetContext,
  now: number = Date.now(),
): ExportAllowance {
  const bonus = ctx.rewards.amountOf(REWARD_EXPORT_QUOTA, now);
  if (!ctx.adsLive || ctx.premium) {
    return { allowed: true, used: ctx.budget.usedToday(now), limit: null, bonus };
  }
  const limit = exportLimitWithAdBonus(FREE_DAILY_EXPORTS, ctx.rewards, now);
  const used = ctx.budget.usedToday(now);
  return { allowed: limit === null || used < limit, used, limit, bonus };
}
